import { useState, useEffect, useRef, useCallback } from "react";
import { fetchResources } from "../services/chatService";
import {
  FAILED_MESSAGE_PREFIX,
  IN_PROCESS_MESSAGE,
  MAX_SELECTED_FILES,
  filterResourcesBySearch,
  getFailedFiles,
  hasInProcessFiles,
  normalizeResource,
  pruneStaleSelections,
  sortByParentPathThenName,
  syncSelectedWithAvailable,
} from "../utils/resourceUtils";

const parsePropertyId = (propertyIdKey) => {
  if (!propertyIdKey) {
    return 0;
  }
  const parsed = parseInt(propertyIdKey.toString(), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const useFileContext = (authToken, propertyIdKey) => {
  const propertyId = parsePropertyId(propertyIdKey);
  const [availableResources, setAvailableResources] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [resourceSearchTerm, setResourceSearchTerm] = useState("");
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState(null);
  const [validationMessage, setValidationMessage] = useState("");

  const selectedFilesRef = useRef(selectedFiles);
  const availableResourcesRef = useRef(availableResources);
  const previousPropertyIdRef = useRef(propertyId);

  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  useEffect(() => {
    availableResourcesRef.current = availableResources;
  }, [availableResources]);

  const applyResourceList = useCallback((resources) => {
    const normalized = sortByParentPathThenName(
      (Array.isArray(resources) ? resources : []).map(normalizeResource)
    );
    setAvailableResources(normalized);
    setSelectedFiles((prev) => pruneStaleSelections(syncSelectedWithAvailable(prev, normalized), normalized));
    return normalized;
  }, []);

  const loadResources = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!authToken) {
      return [];
    }
    if (!silent) {
      setResourceLoading(true);
    }
    setResourceError(null);
    try {
      const data = await fetchResources(authToken, propertyId);
      return applyResourceList(data);
    } catch (error) {
      console.error("Failed to fetch resources:", error);
      if (!silent) {
        setResourceError("Could not load files. Please try again.");
      }
      throw error;
    } finally {
      if (!silent) {
        setResourceLoading(false);
      }
    }
  }, [authToken, propertyId, applyResourceList]);

  useEffect(() => {
    if (!authToken) {
      return;
    }
    if (previousPropertyIdRef.current !== propertyId) {
      previousPropertyIdRef.current = propertyId;
      setSelectedFiles([]);
      setResourceSearchTerm("");
      setValidationMessage("");
    }
    console.log("[useFileContext] loadResources START", { propertyId });
    loadResources()
      .then((resources) => {
        console.log("[useFileContext] loadResources OK", {
          propertyId,
          count: Array.isArray(resources) ? resources.length : 0,
        });
      })
      .catch((error) => {
        console.warn("[useFileContext] loadResources FAIL", {
          propertyId,
          error: error?.message || String(error),
        });
      });
  }, [authToken, propertyId, loadResources]);

  const getFileIds = useCallback(
    () => selectedFilesRef.current.map((file) => file.connected_id),
    []
  );

  const selectFile = useCallback((resource) => {
    const normalized = normalizeResource(resource);
    setSelectedFiles((prev) => {
      if (prev.length >= MAX_SELECTED_FILES) {
        return prev;
      }
      if (prev.some((file) => file.connected_id === normalized.connected_id)) {
        return prev;
      }
      const next = [...prev, normalized];
      selectedFilesRef.current = next;
      return next;
    });
    setResourceSearchTerm("");
    setValidationMessage("");
  }, []);

  const removeFile = useCallback((connectedId) => {
    setSelectedFiles((prev) => {
      const next = prev.filter((file) => file.connected_id !== connectedId);
      selectedFilesRef.current = next;
      return next;
    });
    setValidationMessage("");
  }, []);

  const runWithFileValidation = useCallback(
    async (executeFn, options = {}) => {
      const { onDocContext } = options;

      const notifyDocContext = (files) => {
        if (files.length > 0 && typeof onDocContext === "function") {
          onDocContext(files);
        }
      };

      const synced = syncSelectedWithAvailable(selectedFilesRef.current, availableResourcesRef.current);
      if (synced !== selectedFilesRef.current) {
        setSelectedFiles(synced);
        selectedFilesRef.current = synced;
      }

      const failed = getFailedFiles(synced);
      if (failed.length > 0) {
        setValidationMessage(FAILED_MESSAGE_PREFIX + failed.map((file) => file.name).join(", "));
        return false;
      }

      if (hasInProcessFiles(synced)) {
        setValidationMessage(IN_PROCESS_MESSAGE);
        return false;
      }

      setValidationMessage("");
      notifyDocContext(synced);
      executeFn();
      return true;
    },
    []
  );

  const selectedIds = new Set(selectedFiles.map((file) => file.connected_id));
  const filteredResources = filterResourcesBySearch(availableResources, resourceSearchTerm, selectedIds);

  return {
    propertyId,
    availableResources,
    selectedFiles,
    resourceSearchTerm,
    setResourceSearchTerm,
    resourceLoading,
    resourceError,
    validationMessage,
    setValidationMessage,
    filteredResources,
    loadResources,
    selectFile,
    removeFile,
    getFileIds,
    runWithFileValidation,
  };
};
